import { Upload, Settings2, Download } from 'lucide-react';
import { useInView } from '../hooks/useInView';

const steps = [
  {
    step: '01',
    icon: Upload,
    title: 'Upload Your File',
    description:
      'Drag and drop or browse to select any image or PDF from your device. Supported formats include JPG, PNG, WebP, and PDF.',
    color: '#0C66FA',
    bg: 'rgba(12, 102, 250, 0.08)',
  },
  {
    step: '02',
    icon: Settings2,
    title: 'Edit, Convert, or Compress',
    description:
      'Choose your tool — build a fillable PDF, convert your image format, or dial in your compression quality with a live preview.',
    color: '#732EEC',
    bg: 'rgba(115, 46, 236, 0.08)',
  },
  {
    step: '03',
    icon: Download,
    title: 'Download Instantly',
    description:
      'Your processed file is ready in seconds. Download it directly to your device or access it anytime from your FileMingle library.',
    color: '#16A34A',
    bg: 'rgba(22, 163, 74, 0.08)',
  },
];

function StepCard({ step, index, total }) {
  const [ref, inView] = useInView(0.1);
  const Icon = step.icon;

  return (
    <div className="relative flex-1">
      {/* Connector line (desktop) */}
      {index < total - 1 && (
        <div className="hidden lg:block absolute top-10 left-[calc(50%+52px)] right-0 h-px bg-gradient-to-r from-slate-200 to-slate-100" />
      )}

      <div
        ref={ref}
        className="flex flex-col items-center text-center px-4"
        style={{
          opacity: inView ? 1 : 0,
          transform: inView ? 'translateY(0)' : 'translateY(28px)',
          transition: `opacity 0.55s ease ${index * 0.15}s, transform 0.55s ease ${index * 0.15}s`,
        }}
      >
        {/* Step number badge */}
        <div className="relative mb-5">
          <div
            className="w-20 h-20 rounded-2xl flex items-center justify-center shadow-lg"
            style={{ background: step.bg, border: `1.5px solid ${step.color}22` }}
          >
            <Icon size={30} style={{ color: step.color }} />
          </div>
          <span
            className="absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-black shadow-md"
            style={{ background: step.color }}
          >
            {index + 1}
          </span>
        </div>

        <h3 className="text-lg font-bold text-slate-900 mb-2">{step.title}</h3>
        <p className="text-sm text-slate-500 leading-relaxed max-w-xs">
          {step.description}
        </p>
      </div>
    </div>
  );
}

export default function HowItWorksSection() {
  const [headRef, headInView] = useInView();

  return (
    <section id="how-it-works" className="py-24 sm:py-32 bg-white">
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        {/* Heading */}
        <div
          ref={headRef}
          className="text-center mb-16"
          style={{
            opacity: headInView ? 1 : 0,
            transform: headInView ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.6s ease, transform 0.6s ease',
          }}
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-brand-indigo/8 text-brand-indigo text-xs font-bold tracking-widest uppercase mb-4">
            How It Works
          </span>
          <h2 className="text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight mb-4">
            Ready in{' '}
            <span className="text-gradient">Three Simple Steps</span>
          </h2>
          <p className="text-lg text-slate-500 max-w-md mx-auto leading-relaxed">
            No learning curve. No complicated menus. Just results.
          </p>
        </div>

        {/* Steps */}
        <div className="flex flex-col lg:flex-row items-start gap-10 lg:gap-0">
          {steps.map((step, i) => (
            <StepCard key={step.step} step={step} index={i} total={steps.length} />
          ))}
        </div>
      </div>
    </section>
  );
}
